import React from "react";
import { Link } from "react-router-dom";
import { Filter, Star, MapPin, Clock4, MessageCircle, BadgeCheck, ChevronDown, Crown, Languages } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { PROVIDERS, FORM_OPTIONS } from "../lib/mockData";

export default function Compare() {
  return (
    <div className="surface-cream min-h-screen" data-testid="compare-page">
      <Header />

      <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-10">
        <div className="mb-8">
          <div className="chip mb-3">Your innovation brief</div>
          <h1 className="font-display font-bold text-3xl sm:text-4xl text-[#0A2520] mb-2">"AI-powered crop disease detection"</h1>
          <p className="text-sm text-[#5A6B66]">Utility Patent · Software & AI · Startup (DPIIT) · Bengaluru · <button className="underline">edit brief</button></p>
        </div>

        <div className="grid lg:grid-cols-[280px_1fr] gap-8">
          <aside className="card-flat p-5 h-fit lg:sticky lg:top-[90px]" data-testid="filters">
            <div className="flex items-center justify-between mb-5">
              <div className="font-display font-semibold text-[#0A2520] flex items-center gap-2"><Filter size={16}/> Filters</div>
              <button className="text-xs text-[#0E6E5E] font-semibold">Reset</button>
            </div>

            <Section title="Price range">
              <div className="flex gap-2 mb-3">
                <input defaultValue="₹5,000" className="input text-sm py-2"/>
                <input defaultValue="₹50,000" className="input text-sm py-2"/>
              </div>
              <div className="h-1.5 rounded-full bg-[#F0F4F0] relative">
                <div className="absolute left-[10%] right-[35%] h-full rounded-full bg-[#0E6E5E]"/>
              </div>
            </Section>

            <Section title="Turnaround">
              {["Under 7 days", "7-14 days", "14-30 days", "30+ days"].map((o, i) => <CB key={o} label={o} checked={i === 0 || i === 1}/>)}
            </Section>

            <Section title="Minimum rating">
              <div className="flex gap-1">
                {[3, 3.5, 4, 4.5, 5].map((r) => (
                  <button key={r} className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold ${r === 4.5 ? "bg-[#0E6E5E] text-white" : "border border-[#E1E8E2] text-[#5A6B66]"}`}>{r}+</button>
                ))}
              </div>
            </Section>

            <Section title="Location">
              {FORM_OPTIONS.cities.slice(0, 5).map((c, i) => <CB key={c} label={c} checked={i === 2}/>)}
            </Section>

            <Section title="Language">
              {FORM_OPTIONS.languages.slice(0, 5).map((l, i) => <CB key={l} label={l} checked={i < 2}/>)}
            </Section>

            <Section title="Specialization">
              {["AI/ML", "Software", "Biotech", "Pharma", "Mechanical"].map((s, i) => <CB key={s} label={s} checked={i < 2}/>)}
            </Section>
          </aside>

          <main>
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
              <div className="text-sm text-[#5A6B66]">Showing <span className="font-semibold text-[#0A2520]">{PROVIDERS.length} quotes</span> matched to your brief</div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-[#5A6B66]">Sort:</span>
                <button className="btn-outline text-sm">Best match <ChevronDown size={14}/></button>
              </div>
            </div>

            <div className="space-y-4">
              {PROVIDERS.slice(0, 5).map((p, idx) => (
                <div key={p.id} className="card p-6 relative" data-testid={`compare-${p.id}`}>
                  {p.premium && <div className="absolute top-4 right-4 chip chip-gold"><Crown size={10}/> Premium</div>}
                  <div className="grid md:grid-cols-[auto_1fr_auto] gap-6">
                    <img src={p.avatar} alt={p.name} className="w-20 h-20 rounded-2xl object-cover"/>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <Link to={`/attorneys/${p.id}`} className="font-display text-xl font-bold text-[#0A2520] hover:text-[#0E6E5E] transition truncate">{p.name}</Link>
                        {p.verified && <span className="chip inline-flex items-center gap-1"><BadgeCheck size={11}/> Verified</span>}
                        {idx === 0 && <span className="chip-dark chip">Best match</span>}
                      </div>
                      <div className="text-sm text-[#5A6B66] mb-3">{p.tagline}</div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4 text-xs">
                        <Cell label="Rating" value={<span className="flex items-center gap-1"><Star size={12} className="fill-[#7CC242] text-[#7CC242]"/>{p.rating} ({p.reviews})</span>}/>
                        <Cell label="Patents filed" value={p.patentsFiled.toLocaleString()}/>
                        <Cell label="Location" value={<span className="flex items-center gap-1"><MapPin size={10}/>{p.location}</span>}/>
                        <Cell label="ETA" value={<span className="flex items-center gap-1"><Clock4 size={10}/>{p.turnaround}</span>}/>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        {p.specializations.map((s) => <span key={s} className="chip">{s}</span>)}
                        <span className="text-xs text-[#5A6B66] flex items-center gap-1 ml-2"><Languages size={11}/> {p.languages.join(", ")}</span>
                      </div>
                    </div>
                    <div className="md:text-right flex md:flex-col items-start md:items-end justify-between md:justify-start gap-3 md:min-w-[170px] pt-3 md:pt-0 border-t md:border-t-0 md:border-l border-[#F0F4F0] md:pl-6">
                      <div>
                        <div className="text-xs text-[#5A6B66]">Tentative quote</div>
                        <div className="font-display text-3xl font-extrabold text-[#0A2520]">₹{p.priceFrom.toLocaleString('en-IN')}</div>
                        <div className="text-xs text-[#5A6B66]">Responds {p.response}</div>
                      </div>
                      <div className="flex md:flex-col gap-2 md:w-full">
                        <Link to={`/attorneys/${p.id}`} className="btn-primary text-sm whitespace-nowrap" data-testid={`schedule-${p.id}`}>Schedule call</Link>
                        <button className="btn-outline text-sm"><MessageCircle size={14}/> Message</button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-8">
              <Link to="/attorneys" className="btn-outline">View full attorney directory</Link>
            </div>
          </main>
        </div>
      </div>

      <Footer />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-5 pb-5 border-b border-[#F0F4F0] last:border-0 last:mb-0 last:pb-0">
      <div className="font-display text-xs font-bold text-[#0A2520] uppercase tracking-wider mb-3">{title}</div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function CB({ label, checked }) {
  return (
    <label className="flex items-center gap-2.5 text-sm cursor-pointer group">
      <div className={`w-4 h-4 rounded border flex items-center justify-center ${checked ? "bg-[#0E6E5E] border-[#0E6E5E]" : "border-[#DDE5DD] group-hover:border-[#0E6E5E]"}`}>
        {checked && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><path d="M5 12l5 5L20 7"/></svg>}
      </div>
      <span className={checked ? "text-[#0A2520] font-medium" : "text-[#5A6B66]"}>{label}</span>
    </label>
  );
}

function Cell({ label, value }) {
  return (
    <div>
      <div className="text-[#5A6B66] mb-0.5">{label}</div>
      <div className="font-semibold text-[#0A2520]">{value}</div>
    </div>
  );
}
